import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Button,
  IconButton,
  Grid,
  Paper,
  Alert,
  Fab,
  Tooltip,
  useTheme,
  alpha,
  LinearProgress,
  Zoom,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  PersonOutline as PersonIcon,
  WorkOutline as WorkIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useFormValidation } from '../hooks/useFormValidation';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// Days will be populated with translations in the component

// Specializations will be populated with translations in the component

const TrainerProfileForm = ({ profile, onSave, onCancel }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();

  // Translated arrays
  const DAYS_OF_WEEK = [
    t('trainerProfile:days.monday'),
    t('trainerProfile:days.tuesday'),
    t('trainerProfile:days.wednesday'),
    t('trainerProfile:days.thursday'),
    t('trainerProfile:days.friday'),
    t('trainerProfile:days.saturday'),
    t('trainerProfile:days.sunday')
  ];

  const COMMON_SPECIALIZATIONS = [
    t('trainerProfile:specializations.weightTraining'),
    t('trainerProfile:specializations.cardio'),
    t('trainerProfile:specializations.yoga'),
    t('trainerProfile:specializations.pilates'),
    t('trainerProfile:specializations.crossfit'),
    t('trainerProfile:specializations.personalTraining'),
    t('trainerProfile:specializations.groupFitness'),
    t('trainerProfile:specializations.strengthTraining'),
    t('trainerProfile:specializations.hiit'),
    t('trainerProfile:specializations.functionalTraining'),
    t('trainerProfile:specializations.sportsTraining'),
    t('trainerProfile:specializations.rehabilitation')
  ];

  const initialData = {
    specializations: [],
    bio: '',
    weeklyAvailability: []
  };

  const validationRules = {
    specializations: [
      (value) => !value || value.length === 0
        ? t('trainerProfile:validation.specializationsRequired')
        : null
    ],
    bio: [
      (value) => !value?.trim() || value.trim().length < 50
        ? t('trainerProfile:validation.bioTooShort')
        : null,
      (value) => value?.trim().length > 1000
        ? t('trainerProfile:validation.bioTooLong')
        : null
    ],
    weeklyAvailability: [
      (value) => !value || value.length === 0
        ? t('trainerProfile:validation.availabilityRequired')
        : null
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    updateFields,
    handleSubmit,
    setFormData,
    getFieldError,
    hasFieldError
  } = useFormValidation(initialData, validationRules);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (profile) {
      setFormData({
        specializations: profile.specializations || [],
        bio: profile.bio || '',
        weeklyAvailability: profile.weeklyAvailability || []
      });
    }
  }, [profile, setFormData]);

  const handleAddSpecialization = (specialization) => {
    if (specialization && !formData.specializations.includes(specialization)) {
      updateFields({
        specializations: [...formData.specializations, specialization]
      });
    }
  };

  const handleRemoveSpecialization = (specializationToRemove) => {
    updateFields({
      specializations: formData.specializations.filter(spec => spec !== specializationToRemove)
    });
  };

  const handleAddAvailabilitySlot = () => {
    updateFields({
      weeklyAvailability: [
        ...formData.weeklyAvailability,
        {
          day: 'Monday',
          startTime: '09:00',
          endTime: '17:00'
        }
      ]
    });
  };

  const handleRemoveAvailabilitySlot = (index) => {
    updateFields({
      weeklyAvailability: formData.weeklyAvailability.filter((_, i) => i !== index)
    });
  };

  const handleAvailabilityChange = (index, field, value) => {
    const updatedAvailability = formData.weeklyAvailability.map((slot, i) => 
      i === index ? { ...slot, [field]: value } : slot
    );
    updateFields({ weeklyAvailability: updatedAvailability });
  };

  const validateAdditionalFields = () => {
    if (formData.specializations.length === 0) {
      setError('At least one specialization is required');
      return false;
    }

    if (formData.weeklyAvailability.length === 0) {
      setError('At least one availability slot is required');
      return false;
    }

    for (const slot of formData.weeklyAvailability) {
      const startTime = slot.startTime.split(':').map(Number);
      const endTime = slot.endTime.split(':').map(Number);
      const startMinutes = startTime[0] * 60 + startTime[1];
      const endMinutes = endTime[0] * 60 + endTime[1];

      if (endMinutes <= startMinutes) {
        setError(`End time must be after start time for ${slot.day} slot`);
        return false;
      }
    }

    setError('');
    return true;
  };

  const onFormSubmit = async (data) => {
    if (!validateAdditionalFields()) {
      throw new Error('Validation failed');
    }

    setError('');
    setSuccess('');

    const response = await api.post('/trainer-profiles/me', data);
    
    if (response.data.success) {
      setSuccess(response.data.message || 'Profile saved successfully');
      if (onSave) {
        onSave(response.data.data);
      }
    }
  };

  if (user?.role !== 'trainer') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3
        }}
      >
        <Alert 
          severity="error" 
          sx={{ 
            maxWidth: 400,
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body2">
            Only trainers can access this page. Please log in with a trainer account.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const characterProgress = (formData.bio.length / 1000) * 100;
  const isMinimumMet = formData.bio.length >= 50;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 }
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            {profile ? t('trainerProfile:title.edit') : t('trainerProfile:title.create')}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            {profile
              ? t('trainerProfile:description.edit')
              : t('trainerProfile:description.create')
            }
          </Typography>
        </Box>

        {/* Alert Messages */}
        <Collapse in={!!error}>
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        </Collapse>

        <Collapse in={!!success}>
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        </Collapse>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          {/* Specializations Section */}
          <Card 
            sx={{ 
              mb: 4,
              overflow: 'visible',
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    mr: 2
                  }}
                >
                  <WorkIcon />
                </Box>
                <Box>
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    {t('trainerProfile:sections.specializations')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('trainerProfile:sections.specializationsDescription')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={COMMON_SPECIALIZATIONS.filter(spec => !formData.specializations.includes(spec))}
                  value={null}
                  onChange={(event, value) => {
                    if (value) {
                      handleAddSpecialization(value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('trainerProfile:buttons.addSpecialization')}
                      placeholder={t('trainerProfile:placeholders.selectSpecialization')}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <Tooltip title={t('trainerProfile:buttons.addSpecialization')}>
                              <IconButton
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2)
                                  }
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
              
              {formData.specializations.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Selected Specializations ({formData.specializations.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.specializations.map((spec, index) => (
                      <Zoom in key={spec} style={{ transitionDelay: `${index * 100}ms` }}>
                        <Chip
                          label={spec}
                          onDelete={() => handleRemoveSpecialization(spec)}
                          deleteIcon={<DeleteIcon fontSize="small" />}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            fontWeight: 500,
                            '& .MuiChip-deleteIcon': {
                              color: 'inherit',
                              '&:hover': {
                                color: 'error.main'
                              }
                            }
                          }}
                        />
                      </Zoom>
                    ))}
                  </Box>
                </Box>
              )}

              {formData.specializations.length === 0 && (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}`
                  }}
                >
                  <InfoIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No specializations added yet. Add at least one to showcase your expertise.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Bio Section */}
          <Card 
            sx={{ 
              mb: 4,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: 'secondary.main',
                    mr: 2
                  }}
                >
                  <PersonIcon />
                </Box>
                <Box>
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    {t('trainerProfile:sections.bio')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('trainerProfile:sections.bioDescription')}
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={6}
                value={formData.bio}
                onChange={(e) => updateField('bio', e.target.value)}
                placeholder={t('trainerProfile:placeholders.enterBio')}
                error={hasFieldError('bio')}
                helperText={getFieldError('bio')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }
                }}
              />

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(characterProgress, 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.grey[400], 0.3),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: isMinimumMet 
                          ? characterProgress >= 100 ? 'error.main' : 'success.main'
                          : 'warning.main'
                      }
                    }}
                  />
                </Box>
                <Typography 
                  variant="caption" 
                  color={isMinimumMet 
                    ? characterProgress >= 100 ? 'error.main' : 'success.main'
                    : 'warning.main'
                  }
                  fontWeight={500}
                >
                  {formData.bio.length}/1000
                </Typography>
              </Box>
              
              <Typography 
                variant="caption" 
                color={isMinimumMet ? 'success.main' : 'warning.main'}
                sx={{ mt: 1, display: 'block' }}
              >
                {isMinimumMet 
                  ? characterProgress >= 100 
                    ? 'Maximum character limit reached' 
                    : 'Great! You\'ve met the minimum requirement'
                  : `${50 - formData.bio.length} more characters needed`
                }
              </Typography>
            </CardContent>
          </Card>

          {/* Weekly Availability Section */}
          <Card 
            sx={{ 
              mb: 4,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    mr: 2
                  }}
                >
                  <ScheduleIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    {t('trainerProfile:sections.weeklyAvailability')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('trainerProfile:sections.weeklyAvailabilityDescription')}
                  </Typography>
                </Box>
                <Tooltip title={t('trainerProfile:buttons.addAvailability')}>
                  <Fab
                    size="medium"
                    color="primary"
                    onClick={handleAddAvailabilitySlot}
                    sx={{
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </Tooltip>
              </Box>

              {formData.weeklyAvailability.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.weeklyAvailability.map((slot, index) => (
                    <Zoom in key={index} style={{ transitionDelay: `${index * 100}ms` }}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: 2
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Grid container spacing={3} alignItems="center">
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <TextField
                              select
                              fullWidth
                              label="Day"
                              value={slot.day}
                              onChange={(e) => handleAvailabilityChange(index, 'day', e.target.value)}
                              SelectProps={{ native: true }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            >
                              {DAYS_OF_WEEK.map(day => (
                                <option key={day} value={day}>
                                  {day}
                                </option>
                              ))}
                            </TextField>
                          </Grid>
                          
                          <Grid size={{ xs: 6, sm: 3 }}>
                            <TextField
                              type="time"
                              fullWidth
                              label="Start Time"
                              value={slot.startTime}
                              onChange={(e) => handleAvailabilityChange(index, 'startTime', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <TimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />
                                )
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>

                          <Grid size={{ xs: 6, sm: 3 }}>
                            <TextField
                              type="time"
                              fullWidth
                              label="End Time"
                              value={slot.endTime}
                              onChange={(e) => handleAvailabilityChange(index, 'endTime', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <TimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />
                                )
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>
                          
                          <Grid size={{ xs: 12, sm: 3 }} sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                            <Tooltip title="Remove this availability slot">
                              <IconButton
                                onClick={() => handleRemoveAvailabilitySlot(index)}
                                disabled={formData.weeklyAvailability.length === 1}
                                sx={{
                                  color: 'error.main',
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.error.main, 0.2),
                                    transform: 'scale(1.1)'
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    bgcolor: alpha(theme.palette.grey[400], 0.1)
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Zoom>
                  ))}
                </Box>
              ) : (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}`
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    No availability slots added
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add at least one time slot to show when you're available for training sessions.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddAvailabilitySlot}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Your First Slot
                  </Button>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Form Actions */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Ready to save your profile?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Make sure all information is accurate before saving.
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                {onCancel && (
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={onCancel}
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      minWidth: 120
                    }}
                  >
                    {t('trainerProfile:buttons.cancel')}
                  </Button>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={isSubmitting ? null : <SaveIcon />}
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    minWidth: 140,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-1px)'
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5)
                    }
                  }}
                >
                  {isSubmitting ? t('common:loading.saving') : t('trainerProfile:buttons.save')}
                </Button>
              </Box>
            </Box>
          </Paper>
        </form>
      </Box>
    </Box>
  );
};

export default TrainerProfileForm;